import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantAutoRefresh } from '@/hooks/use-tenant-auto-refresh';

export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const isInitialized = useAuthStore((s) => s.isInitialized);

  // Arms the proactive refresh timer for the current access token.
  // Must be called unconditionally (React hook rules) — the hook checks isAuthenticated
  // internally and does nothing when the user is not signed in.
  useTenantAutoRefresh();

  useEffect(() => {
    const { isAuthenticated, accessToken, setInitialUser, clearAuth, setInitialized } =
      useAuthStore.getState();

    if (!isAuthenticated || !accessToken) {
      setInitialized();
      return;
    }

    // Validate the stored access token against the backend.
    // Uses apiClient which will silently refresh via the 401 interceptor if the
    // token has already expired — setInitialUser is still called after the retry succeeds.
    authService
      .getMe()
      .then((user) => {
        // setInitialUser only updates the user profile — tokens in the store are preserved
        // (the 401 interceptor may have already rotated them during getMe if needed).
        setInitialUser(user);
        setInitialized();
      })
      .catch(() => {
        clearAuth();
        setInitialized();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab session sync. Zustand's persist middleware writes auth state to
  // localStorage but does not re-hydrate other tabs on change, so signing out in
  // one tab previously left sibling tabs authenticated in memory. The `storage`
  // event fires only in *other* tabs when localStorage changes: when the shared
  // `sp-auth` entry loses its access token (logout, or an expired-session
  // clear), we drop this tab's in-memory session too. ProtectedRoute then
  // reactively redirects to the login page. Signing in elsewhere is not force-
  // propagated here (no silent cross-tab elevation) — only de-authentication.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'sp-auth') return;
      const { isAuthenticated, clearAuth } = useAuthStore.getState();
      if (!isAuthenticated) return;

      let stillAuthed = false;
      try {
        if (e.newValue) {
          const parsed = JSON.parse(e.newValue) as { state?: { accessToken?: string | null } };
          stillAuthed = Boolean(parsed.state?.accessToken);
        }
      } catch {
        stillAuthed = false; // corrupted entry — treat as signed out
      }

      if (!stillAuthed) clearAuth();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
}
